import React, { useEffect } from 'react';

/**
 * KeyboardShortcutsModal - Reference panel showing all keyboard shortcuts (SAT-77)
 *
 * Features:
 * - Opens with ? key globally
 * - Lists all shortcuts grouped by function
 * - Escape to close
 * - Can be accessed from Settings
 */

const SHORTCUT_GROUPS = [
  {
    title: 'Chat',
    shortcuts: [
      { keys: ['Enter'], description: 'Send message' },
      { keys: ['Shift', 'Enter'], description: 'New line in message' },
      { keys: ['Escape'], description: 'Stop message generation' },
    ]
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['⌘/Ctrl', 'B'], description: 'Toggle sidebar' },
      { keys: ['⌘/Ctrl', 'K'], description: 'Open command palette' },
      { keys: ['?'], description: 'Open keyboard shortcuts' },
    ]
  },
  {
    title: 'Conversation',
    shortcuts: [
      { keys: ['⌘/Ctrl', 'N'], description: 'New conversation' },
      { keys: ['⌘/Ctrl', 'Shift', 'S'], description: 'Search conversations' },
    ]
  },
  {
    title: 'Artifacts',
    shortcuts: [
      { keys: ['Escape'], description: 'Exit fullscreen artifact' },
      { keys: ['⌘/Ctrl', 'C'], description: 'Copy artifact content' },
    ]
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['Escape'], description: 'Close modal/panel' },
      { keys: ['⌘/Ctrl', ','], description: 'Open settings' },
    ]
  },
];

function KeyboardShortcutsModal({ isOpen, onClose }) {
  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SHORTCUT_GROUPS.map((group) => (
              <div key={group.title} className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {group.title}
                </h3>
                <div className="space-y-2">
                  {group.shortcuts.map((shortcut, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2 px-3
                        bg-gray-50 dark:bg-gray-750 rounded-lg"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {shortcut.description}
                      </span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIdx) => (
                          <span key={keyIdx} className="flex items-center">
                            <kbd className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200
                              bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600
                              rounded shadow-xs min-w-[28px] text-center">
                              {key}
                            </kbd>
                            {keyIdx < shortcut.keys.length - 1 && (
                              <span className="mx-1 text-gray-400">+</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer hint */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Press <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm">?</kbd> anytime to see this panel
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-850">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white
              rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default KeyboardShortcutsModal;
